import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Users } from 'lucide-react';
import { useT } from '../../i18n';
import type { HomeMeeting, HomeProjectDetail } from '../../types';
import type { TreeMilestone, TreeTask } from '../../proposals/vue-jalons-taches/api';
import { MONTH_KEYS, dayNum, localYmd } from '../../proposals/timeline-taches/dates';
import { getUrgency } from '../../utils/echeances';
import { CHIP_ICON_OFFSET, clusterEvents, type EventCluster, type TimelineEvent } from '../../utils/geranceHistory';
import { useOpenTaskPanel } from './TaskPanel';

// The project's planning on one time axis, drawn with the Gérance calendar's
// marks: milestones as its bars (done, under way coloured by urgency, to come),
// and under them the same activity rail — a chip per day or group of days,
// meetings (people) and tasks moved to Done (check). Meetings still to come get
// the same chip, outlined dashed. The red line is today. Clicking a milestone
// focuses it in the block below.

const MIN_W = 680;
const HEAD_H = 22;      // month names
const BAR_H = 34;       // as on the Gérance calendar
const LANE_GAP = 6;
const RAIL_H = 28;

type Bar = { m: TreeMilestone; n: number; lane: number; left: number; width: number; state: 'done' | 'active' | 'pending'; done: number; total: number };

/** What the marks mean: the Gérance calendar's legend, with the meetings still to come. */
export function SpineLegend() {
  const { t } = useT();
  return (
    <div className="tl-legend cal-legend home-spine-legend">
      <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-done" />{t('gerancemeta.milestoneStateDone')}</span>
      <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-active cal-bar-urg-normal" />{t('gerancemeta.milestoneUrgencyGreen')}</span>
      <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-active cal-bar-urg-soon" />{t('gerancemeta.milestoneUrgencyOrange')}</span>
      <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-active cal-bar-urg-overdue" />{t('gerancemeta.milestoneUrgencyRed')}</span>
      <span className="tl-legend-item"><span className="cal-legend-swatch cal-bar-pending" />{t('gerancemeta.milestoneStatePending')}</span>
      <span className="tl-legend-sep" aria-hidden="true" />
      <span className="tl-legend-item">
        <span className="cal-chip cal-chip-legend" aria-hidden="true"><span className="cal-chip-seg is-meeting"><Users size={12} /></span></span>
        {t('gerancemeta.markMeeting')}
      </span>
      <span className="tl-legend-item">
        <span className="cal-chip cal-chip-legend is-upcoming" aria-hidden="true"><span className="cal-chip-seg is-meeting"><Users size={12} /></span></span>
        {t('home.spine.legendUpcoming')}
      </span>
      <span className="tl-legend-item">
        <span className="cal-chip cal-chip-legend" aria-hidden="true"><span className="cal-chip-seg is-task"><Check size={12} strokeWidth={3} /></span></span>
        {t('gerancemeta.markTaskDone')}
      </span>
    </div>
  );
}

/** The width available to the chart, kept up to date. */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

const shortDate = (ymd: string) => { const [, mo, d] = ymd.split('-'); return `${Number(d)}.${Number(mo)}`; };

export function ProjectSpine({ projectId, milestones, tasks, meetings, tasksDone, focusId, onFocus }: {
  projectId: number;
  milestones: TreeMilestone[];
  tasks: TreeTask[];
  meetings: HomeMeeting[];
  tasksDone: HomeProjectDetail['tasks_done'];
  focusId: string | null;
  onFocus: (milestoneId: string) => void;
}) {
  const { t } = useT();
  const openTask = useOpenTaskPanel();
  const [scrollRef, measured] = useWidth();
  const W = Math.max(MIN_W, measured);
  const today = dayNum(localYmd(new Date()));

  const view = useMemo(() => {
    const dated = milestones
      .map((m, i) => ({ m, n: i + 1 }))
      .filter(({ m }) => m.status !== 'cancelled' && m.estimated_start && m.estimated_end);
    const now = Date.now();
    const meetingEvents = meetings.map(mt => ({ mt, ymd: localYmd(new Date(mt.start_time)), held: new Date(mt.start_time).getTime() <= now }));
    const doneEvents = tasksDone.map(d => ({ d, ymd: localYmd(new Date(d.at)) }));

    let lo = today - 14, hi = today + 21;
    for (const { m } of dated) { lo = Math.min(lo, dayNum(m.estimated_start!)); hi = Math.max(hi, dayNum(m.estimated_end!) + 1); }
    // Events far from the plan would squeeze it: only those within a month of it count.
    for (const ymd of [...meetingEvents.map(e => e.ymd), ...doneEvents.map(e => e.ymd)]) {
      const day = dayNum(ymd);
      if (day >= lo - 31 && day <= hi + 31) { lo = Math.min(lo, day); hi = Math.max(hi, day + 1); }
    }
    lo -= 7; hi += 7;
    const x = (day: number) => ((day - lo) / (hi - lo)) * W;
    const inRange = (day: number) => day >= lo && day < hi;

    const months: { left: number; label: string }[] = [];
    const first = new Date(lo * 86_400_000);
    let y = first.getUTCFullYear(), mo = first.getUTCMonth() + 1;
    for (;;) {
      if (mo > 11) { mo = 0; y++; }
      const day = Math.floor(Date.UTC(y, mo, 1) / 86_400_000);
      if (day >= hi) break;
      months.push({ left: x(day), label: t(MONTH_KEYS[mo]) + (mo === 0 ? ` ${String(y).slice(2)}` : '') });
      mo++;
    }

    const counts = new Map<string, { done: number; total: number }>();
    for (const task of tasks) {
      if (!task.milestone_id) continue;
      const c = counts.get(task.milestone_id) ?? { done: 0, total: 0 };
      c.total++; if (task.is_done) c.done++;
      counts.set(task.milestone_id, c);
    }

    const laneEnds: number[] = [];
    const bars: Bar[] = dated.map(({ m, n }) => {
      const s = dayNum(m.estimated_start!), e = dayNum(m.estimated_end!) + 1;
      let lane = 0;
      while (laneEnds[lane] != null && laneEnds[lane] > s) lane++;
      laneEnds[lane] = e;
      const c = counts.get(m.id) ?? { done: 0, total: 0 };
      const state = m.status === 'completed' ? 'done' : m.status === 'active' ? 'active' : 'pending';
      return { m, n, lane, left: x(s), width: Math.max(24, x(e) - x(s) - 3), state, ...c };
    });
    const lanes = Math.max(1, laneEnds.length);

    const event = (kind: TimelineEvent['kind'], id: number | null, title: string, by: string | null, ymd: string): TimelineEvent =>
      ({ kind, id, title, by, date: ymd, x: x(dayNum(ymd) + 0.5) });
    const held = clusterEvents([
      ...meetingEvents.filter(e => e.held && inRange(dayNum(e.ymd))).map(e => event('meeting', e.mt.id, e.mt.subject || t('home.meetings.untitled'), null, e.ymd)),
      ...doneEvents.filter(e => inRange(dayNum(e.ymd))).map(e => event('task', e.d.task_id, e.d.title || t('home.who.privateTask'), e.d.by, e.ymd)),
    ]);
    const upcoming = clusterEvents(meetingEvents.filter(e => !e.held && inRange(dayNum(e.ymd)))
      .map(e => event('meeting', e.mt.id, e.mt.subject || t('home.meetings.untitled'), null, e.ymd)));

    const undated = milestones.map((m, i) => ({ m, n: i + 1 }))
      .filter(({ m }) => m.status !== 'cancelled' && (!m.estimated_start || !m.estimated_end));
    const barsH = LANE_GAP + lanes * (BAR_H + LANE_GAP);
    return { months, bars, held, upcoming, today: x(today + 0.5), barsH, height: HEAD_H + barsH + RAIL_H, undated };
  }, [milestones, tasks, meetings, tasksDone, today, W, t]);

  if (milestones.length === 0) {
    return <div className="home-spine-empty">{t('home.spine.noMilestones')}</div>;
  }

  const chip = (c: EventCluster, upcoming: boolean) => {
    const lines = [...c.meetings, ...c.tasks].map(e => `${shortDate(e.date)} · ${e.title}${e.by ? ` (${e.by})` : ''}`);
    const label = [
      c.meetings.length ? t('gerancemeta.markMeetingsTitle', { n: c.meetings.length }) : '',
      c.tasks.length ? t('gerancemeta.markTasksDoneTitle', { n: c.tasks.length }) : '',
    ].filter(Boolean).join(', ');
    const single = c.meetings.length === 0 && c.tasks.length === 1 && c.tasks[0].id != null && c.tasks[0].title !== t('home.who.privateTask') ? c.tasks[0].id : null;
    return (
      <button key={`${upcoming ? 'u' : 'h'}${Math.round(c.x)}`} type="button"
        className={`cal-chip${upcoming ? ' is-upcoming' : ''}`} style={{ left: c.x - CHIP_ICON_OFFSET }}
        aria-label={label} title={[label, ...lines.slice(0, 8), lines.length > 8 ? '…' : ''].filter(Boolean).join('\n')}
        onClick={single != null && openTask ? () => openTask(single, projectId) : undefined}>
        {c.meetings.length > 0 && (
          <span className="cal-chip-seg is-meeting"><Users size={12} />{c.meetings.length > 1 && <span className="cal-chip-n">{c.meetings.length}</span>}</span>
        )}
        {c.tasks.length > 0 && (
          <span className="cal-chip-seg is-task"><Check size={12} strokeWidth={3} />{c.tasks.length > 1 && <span className="cal-chip-n">{c.tasks.length}</span>}</span>
        )}
      </button>
    );
  };

  return (
    <div className="home-spine">
      <SpineLegend />
      <div className="home-spine-scroll" ref={scrollRef}>
        <div className="home-spine-canvas" style={{ width: W, height: view.height }}>
          {view.months.map(mo => (
            <div key={mo.left} className="home-spine-month" style={{ left: mo.left, bottom: RAIL_H }}>
              <span>{mo.label}</span>
            </div>
          ))}
          {view.bars.map(b => {
            const urgency = b.state === 'active' && b.m.estimated_end ? getUrgency(b.m.estimated_end) : null;
            return (
              <button
                key={b.m.id}
                type="button"
                className={`cal-bar cal-bar-${b.state}${urgency ? ` cal-bar-urg-${urgency}` : ''} home-spine-bar${b.m.id === focusId ? ' is-focused' : ''}`}
                style={{ left: b.left, width: b.width, height: BAR_H, top: HEAD_H + LANE_GAP + b.lane * (BAR_H + LANE_GAP) }}
                aria-pressed={b.m.id === focusId}
                title={`${t('home.spine.barTitle', { n: b.n, name: b.m.name || t('milestones.untitled'), done: b.done, total: b.total })}\n${b.m.estimated_start} → ${b.m.estimated_end}`}
                onClick={() => onFocus(b.m.id)}
              >
                <span className="cal-bar-pattern" />
                <span className="cal-bar-body">
                  <span className="cal-bar-label">{t('home.spine.barLabel', { n: b.n, name: b.m.name || t('milestones.untitled') })}</span>
                  {b.total > 0 && <span className="home-spine-bar-count">{b.done}/{b.total}</span>}
                </span>
              </button>
            );
          })}
          {/* Activity rail: what happened on the project, on its day (and the meetings to come). */}
          <div className="cal-rail" style={{ height: RAIL_H }}>
            {view.held.map(c => chip(c, false))}
            {view.upcoming.map(c => chip(c, true))}
          </div>
          <div className="cal-today-line" style={{ left: view.today }} title={t('planning.today')} />
        </div>
      </div>
      {view.undated.length > 0 && (
        <div className="home-spine-undated">
          {t('home.spine.undated')}{' '}
          {view.undated.map(({ m, n }, i) => (
            <span key={m.id}>
              {i > 0 && ', '}
              <button type="button" className="home-link-btn" onClick={() => onFocus(m.id)}>
                {t('home.spine.barLabel', { n, name: m.name || t('milestones.untitled') })}
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
